// Lucka 9: Säkerhetsintroduktion per person – vilka individer som introducerats till arbetsplatsen.
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import DocumentHeader from "./DocumentHeader";
import { UserCheck, Plus, Trash2, AlertTriangle, Users, FileDown } from "lucide-react";
import { generateInductionsWordDocument } from "@/lib/documentGenerator/basuExecutionGenerators";

interface Props {
  projectId: string;
}

interface Induction {
  id: string;
  stage_id: string | null;
  person_name: string;
  company: string | null;
  trade: string | null;
  induction_date: string;
  language: string;
  held_by: string | null;
  valid_to: string | null;
  topics: string[];
  notes: string | null;
}

interface Stage {
  id: string;
  name: string;
}

const TOPICS = [
  "Arbetsmiljöplanens innehåll",
  "Skydds- och ordningsregler",
  "Personlig skyddsutrustning",
  "Arbetsplatsens riskområden och avspärrningar",
  "Utrymningsvägar och samlingsplats",
  "Larm, första hjälpen och krisrutin",
  "Rapportering av tillbud och olyckor",
  "Kontaktpersoner: Bas-U och skyddsombud",
];

const LANGUAGES = [
  { value: "sv", label: "Svenska" },
  { value: "en", label: "Engelska" },
  { value: "pl", label: "Polska" },
  { value: "uk", label: "Ukrainska" },
  { value: "ar", label: "Arabiska" },
  { value: "other", label: "Annat språk" },
];

const NO_STAGE = "__none__";

const SafetyInductions: React.FC<Props> = ({ projectId }) => {
  const [rows, setRows] = useState<Induction[]>([]);
  const [stages, setStages] = useState<Stage[]>([]);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    const [inductions, stageData] = await Promise.all([
      supabase
        .from("safety_inductions")
        .select(
          "id, stage_id, person_name, company, trade, induction_date, language, held_by, valid_to, topics, notes"
        )
        .eq("project_id", projectId)
        .order("induction_date", { ascending: false }),
      supabase.from("project_stages").select("id, name").eq("project_id", projectId).order("name"),
    ]);

    if (inductions.error) toast.error("Kunde inte läsa introduktionerna");
    setRows(
      ((inductions.data as unknown as Induction[] | null) ?? []).map((r) => ({
        ...r,
        topics: Array.isArray(r.topics) ? r.topics : [],
      }))
    );
    setStages((stageData.data as Stage[] | null) ?? []);
    setLoading(false);
  }, [projectId]);

  useEffect(() => {
    load();
  }, [load]);

  const add = async () => {
    const { data: auth } = await supabase.auth.getUser();
    const { error } = await supabase.from("safety_inductions").insert({
      project_id: projectId,
      person_name: "Ny person",
      created_by: auth?.user?.id ?? null,
    });
    if (error) return toast.error("Kunde inte lägga till personen");
    load();
  };

  const save = async (id: string, patch: Partial<Induction>) => {
    const { error } = await supabase
      .from("safety_inductions")
      .update(patch as never)
      .eq("id", id);
    if (error) return toast.error("Kunde inte spara");
    setRows((prev) => prev.map((r) => (r.id === id ? { ...r, ...patch } : r)));
  };

  const remove = async (id: string) => {
    const { error } = await supabase.from("safety_inductions").delete().eq("id", id);
    if (error) return toast.error("Kunde inte ta bort");
    setRows((prev) => prev.filter((r) => r.id !== id));
  };

  const toggleTopic = (row: Induction, topic: string, checked: boolean) => {
    const next = checked ? [...row.topics, topic] : row.topics.filter((t) => t !== topic);
    save(row.id, { topics: next });
  };

  const expired = (r: Induction) => !!r.valid_to && new Date(r.valid_to).getTime() < Date.now();

  const byCompany = useMemo(() => {
    const map = new Map<string, { total: number; complete: number; expired: number }>();
    rows.forEach((r) => {
      const key = r.company?.trim() || "Utan angivet företag";
      const entry = map.get(key) ?? { total: 0, complete: 0, expired: 0 };
      entry.total += 1;
      if (r.topics.length === TOPICS.length) entry.complete += 1;
      if (expired(r)) entry.expired += 1;
      map.set(key, entry);
    });
    return Array.from(map.entries()).sort(([a], [b]) => a.localeCompare(b, "sv"));
  }, [rows]);

  const stageName = (id: string | null) => stages.find((s) => s.id === id)?.name ?? "Hela projektet";

  const exportWord = async () => {
    try {
      await generateInductionsWordDocument(
        projectId,
        rows.map((r) => ({ ...r, stageName: stageName(r.stage_id) }))
      );
    } catch {
      toast.error("Kunde inte skapa Word-dokumentet");
    }
  };

  return (
    <div className="space-y-6">
      <DocumentHeader
        docNumber="15c"
        title="Säkerhetsintroduktion per person"
        description="Alla som arbetar på arbetsplatsen ska ha introducerats till arbetsmiljöplanen och ordningsreglerna. Här registreras vem, när, på vilket språk och vad introduktionen omfattade."
        icon={UserCheck}
        projectId={projectId}
        exportHistory={{ documentType: "safety_induction" }}
        actions={
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={exportWord}>
              <FileDown className="mr-2 h-4 w-4" /> Exportera Dok 15c
            </Button>
            <Button onClick={add}>
              <Plus className="mr-2 h-4 w-4" /> Lägg till person
            </Button>
          </div>
        }
      />

      {rows.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="flex items-center gap-2 text-base">
              <Users className="h-4 w-4" /> Sammanställning per företag
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {byCompany.map(([company, s]) => (
              <div
                key={company}
                className="flex flex-wrap items-center justify-between gap-2 rounded-md border p-3"
              >
                <span className="text-sm font-medium">{company}</span>
                <div className="flex flex-wrap gap-2">
                  <Badge variant="outline">{s.total} personer</Badge>
                  <Badge variant={s.complete === s.total ? "secondary" : "destructive"}>
                    {s.complete} av {s.total} har full introduktion
                  </Badge>
                  {s.expired > 0 && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" /> {s.expired} utgångna
                    </Badge>
                  )}
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {loading ? (
        <p className="text-sm text-muted-foreground">Laddar…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            Ingen är registrerad ännu. Lägg till varje person som fått introduktion till
            arbetsplatsen.
          </CardContent>
        </Card>
      ) : (
        rows.map((row) => (
          <Card key={row.id}>
            <CardHeader className="pb-2">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <CardTitle className="text-base">{row.person_name}</CardTitle>
                <div className="flex flex-wrap items-center gap-2">
                  <Badge variant="outline">{stageName(row.stage_id)}</Badge>
                  <Badge variant={row.topics.length === TOPICS.length ? "secondary" : "destructive"}>
                    {row.topics.length} av {TOPICS.length} punkter
                  </Badge>
                  {expired(row) && (
                    <Badge variant="destructive" className="gap-1">
                      <AlertTriangle className="h-3 w-3" /> Utgången
                    </Badge>
                  )}
                  <Button size="sm" variant="ghost" onClick={() => remove(row.id)}>
                    <Trash2 className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="grid gap-3 md:grid-cols-3">
                <div className="space-y-1">
                  <Label>Namn</Label>
                  <Input
                    defaultValue={row.person_name}
                    onBlur={(e) => save(row.id, { person_name: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Företag</Label>
                  <Input
                    defaultValue={row.company ?? ""}
                    onBlur={(e) => save(row.id, { company: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Yrke eller roll</Label>
                  <Input
                    defaultValue={row.trade ?? ""}
                    onBlur={(e) => save(row.id, { trade: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Datum för introduktion</Label>
                  <Input
                    type="date"
                    defaultValue={row.induction_date ?? ""}
                    onBlur={(e) => save(row.id, { induction_date: e.target.value })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Giltig till (om tidsbegränsad)</Label>
                  <Input
                    type="date"
                    defaultValue={row.valid_to ?? ""}
                    onBlur={(e) => save(row.id, { valid_to: e.target.value || null })}
                  />
                </div>
                <div className="space-y-1">
                  <Label>Språk</Label>
                  <Select
                    value={row.language}
                    onValueChange={(v) => save(row.id, { language: v })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {LANGUAGES.map((l) => (
                        <SelectItem key={l.value} value={l.value}>
                          {l.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-1">
                  <Label>Hölls av</Label>
                  <Input
                    defaultValue={row.held_by ?? ""}
                    onBlur={(e) => save(row.id, { held_by: e.target.value })}
                  />
                </div>
                <div className="space-y-1 md:col-span-2">
                  <Label>Etapp</Label>
                  <Select
                    value={row.stage_id ?? NO_STAGE}
                    onValueChange={(v) => save(row.id, { stage_id: v === NO_STAGE ? null : v })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Hela projektet" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_STAGE}>Hela projektet</SelectItem>
                      {stages.map((s) => (
                        <SelectItem key={s.id} value={s.id}>
                          {s.name}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label>Introduktionen omfattade</Label>
                <div className="grid gap-2 md:grid-cols-2">
                  {TOPICS.map((topic) => (
                    <label key={topic} className="flex items-start gap-2 text-sm">
                      <Checkbox
                        checked={row.topics.includes(topic)}
                        onCheckedChange={(c) => toggleTopic(row, topic, c === true)}
                      />
                      <span>{topic}</span>
                    </label>
                  ))}
                </div>
              </div>

              <div className="space-y-1">
                <Label>Anteckning</Label>
                <Textarea
                  defaultValue={row.notes ?? ""}
                  onBlur={(e) => save(row.id, { notes: e.target.value })}
                  placeholder="Tolk användes, särskilda instruktioner, kompletterande introduktion vid etappbyte…"
                />
              </div>
            </CardContent>
          </Card>
        ))
      )}
    </div>
  );
};

export default SafetyInductions;
